/**
 * Pure utility functions for club visual presentation.
 */

import type { Court } from '@/types/club.model';

/**
 * Per-sport color palette for chips. Keyed by the uppercased sport uid so it
 * tolerates whatever casing the API returns ('padel' / 'PADEL'). Unknown
 * sports fall back to the neutral gray tokens.
 */
interface SportVisual {
  soft: string;
  ink: string;
  dot: string;
}

const SPORT_VISUALS: Record<string, SportVisual> = {
  PADEL: { soft: '#EAF1FC', ink: '#1F66D9', dot: '#1F66D9' },
  TENIS: { soft: '#E7F6ED', ink: '#0F7A38', dot: '#22C55E' },
  TENNIS: { soft: '#E7F6ED', ink: '#0F7A38', dot: '#22C55E' },
  SQUASH: { soft: '#FBF0DD', ink: '#9A5408', dot: '#E08A1E' },
};

const SPORT_FALLBACK: SportVisual = { soft: '#EEF0F2', ink: '#5B6675', dot: '#A3ACB9' };

export function getSportVisual(uid: string): SportVisual {
  return SPORT_VISUALS[uid?.toUpperCase()] ?? SPORT_FALLBACK;
}

export interface SportGroup {
  uid: string;
  name: string;
  count: number;
  minPrice: number;
}

/**
 * Groups a club's courts by sport → [{ uid, name, count, minPrice }], sorted by
 * count descending. Used by the sport chips and the per-sport breakdown.
 */
export function courtsBySport(courts: Court[]): SportGroup[] {
  const map = new Map<string, SportGroup>();
  courts.forEach((court) => {
    const uid = court.sport?.uid ?? 'OTHER';
    const price = court.price?.value ?? Infinity;
    const group = map.get(uid);
    if (group) {
      group.count += 1;
      group.minPrice = Math.min(group.minPrice, price);
    } else {
      map.set(uid, {
        uid,
        name: court.sport?.name ?? 'Otros',
        count: 1,
        minPrice: price,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

const arsFormatter = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });

/** Formats a price as whole ARS pesos, e.g. 48000 → "$48.000". */
export function fmtArs(value: number): string {
  return `$${arsFormatter.format(Math.round(value))}`;
}

/**
 * Returns up to 2 uppercase letters from the first 2 words that start with
 * an alphabetic character (including accented chars á-ú).
 * E.g. "World Padel Center" → "WP", "Roca Club" → "RC"
 */
export function getInitials(name: string): string {
  if (!name) return '';
  const alphaStart = /^[a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/u;
  const words = name.trim().split(/\s+/).filter((w) => alphaStart.test(w));
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/**
 * Deterministic hash of `id` → integer 0–359 (same id always same hue).
 */
export function getClubHue(id: string): number {
  if (!id) return 0;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return hash % 360;
}
